import { useParams, useSearchParams } from "react-router";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "~/components/ui/accordion";
import dohaChopaiFilterData from "~/data/dohaChopaiFilter.json";
import { useFontStyle } from "~/hook/useFontStyle";
import { cn } from "~/lib/utils";
import { MstPersonType } from "~/type";
import { LANG } from "~/utils/constant";
import ClientOnly from "../shared-components/ClentOnly";
import { SheetClose } from "../ui/sheet";

interface DohaChopaiFilterProps {
  onApplyTypeFilter?: (value: string) => void;
  onApplyCategoryFilter?: (value: number | string) => void;
  onApplySourceFilter?: (value: number | string) => void;
  onClearAllFilters?: () => void;
  categoryData?: MstPersonType[];
  chopaiSource?: MstPersonType[];
  isMobile?: boolean; // Add mobile mode prop
  handleApplyFilters?: () => void;
  tempFilters?: {
    type: string | null;
    category: number | null;
    source: number | null;
  }; // Add temp filters for mobile mode
}

const DohaChopaiFilter = ({
  onApplyTypeFilter,
  onApplyCategoryFilter,
  onClearAllFilters,
  onApplySourceFilter,
  categoryData = [],
  chopaiSource = [],
  isMobile = false,
  handleApplyFilters,
  tempFilters,
}: DohaChopaiFilterProps) => {
  const { lang } = useParams();
  const { normalInter, medium } = useFontStyle();
  const [searchParams, setSearchParams] = useSearchParams();
  const [checkedItems, setCheckedItems] = useState<{ [key: string]: boolean }>({});
  const [checkedCategories, setCheckedCategories] = useState<{ [key: number]: boolean }>({});
  const [checkedSources, setCheckedSources] = useState<{ [key: number]: boolean }>({});
  const [showAllCategories, setShowAllCategories] = useState(false);

  const INITIAL_CATEGORY_DISPLAY = 5;

  // Initialize checked items from URL params
  useEffect(() => {
    const typeParam = searchParams.get("type");
    const categoryParam = searchParams.get("category");
    const source = searchParams.get("source");

    if (typeParam) {
      const newCheckedItems: { [key: string]: boolean } = {};
      dohaChopaiFilterData.forEach((item) => {
        // Check if the URL param matches either English name, Gujarati name, or type
        const isMatch =
          item.enName.toLowerCase() === typeParam.toLowerCase() || item.guName === typeParam || item.type.toLowerCase() === typeParam.toLowerCase();
        newCheckedItems[item.type] = isMatch;
      });
      setCheckedItems(newCheckedItems);
    } else {
      // Reset checked items when no type param
      setCheckedItems({});
    }

    if (categoryParam && categoryData.length > 0) {
      const newCheckedCategories: { [key: number]: boolean } = {};
      // Convert category text from URL back to ID
      const foundCategory = categoryData.find((cat) => cat.text.toLowerCase().replace(/\s+/g, "-") === categoryParam);
      if (foundCategory) {
        categoryData.forEach((cat) => {
          newCheckedCategories[cat.id] = cat.id === foundCategory.id;
        });
        setCheckedCategories(newCheckedCategories);
      }
    } else {
      // Reset checked categories when no category param
      setCheckedCategories({});
    }

    if (source && chopaiSource && chopaiSource.length > 0) {
      const newCheckedSources: { [key: number]: boolean } = {};
      // Convert source text from URL back to ID
      const foundSource = chopaiSource.find((src) => src.text.toLowerCase().replace(/\s+/g, "-") === source);
      if (foundSource) {
        chopaiSource.forEach((src) => {
          newCheckedSources[src.id] = src.id === foundSource.id;
        });
        setCheckedSources(newCheckedSources);
      }
    } else {
      // Reset checked sources when no source param
      setCheckedSources({});
    }
  }, [searchParams, chopaiSource, lang]); // Added lang dependency

  const handleCheckboxChange = (type: string, checked: boolean) => {
    const newCheckedItems = { ...checkedItems };

    // Clear all other checkboxes (since only one type can be selected at a time)
    Object.keys(newCheckedItems).forEach((key) => {
      newCheckedItems[key] = false;
    });

    // Set the current checkbox
    newCheckedItems[type] = checked;
    setCheckedItems(newCheckedItems);

    // Update URL params only if not in mobile mode
    if (!isMobile) {
      const currentParams = new URLSearchParams(searchParams.toString());
      currentParams.delete("id");
      if (checked) {
        // Find the item to get language-specific text for URL
        const selectedItem = dohaChopaiFilterData.find((item) => item.type === type);
        if (selectedItem) {
          const urlParamValue = lang === LANG.gujarati ? selectedItem.guName : selectedItem.enName;
          currentParams.set("type", urlParamValue);
        }
      } else {
        currentParams.delete("type");
      }
      setSearchParams(currentParams, { replace: true });
    }

    // Call parent callback with English type value for server
    if (onApplyTypeFilter) {
      onApplyTypeFilter(checked ? type : "");
    }
  };

  const handleCategoryChange = (categoryId: number, checked: boolean) => {
    const newCheckedCategories = { ...checkedCategories };

    // Clear all other category checkboxes (since only one category can be selected at a time)
    Object.keys(newCheckedCategories).forEach((key) => {
      newCheckedCategories[parseInt(key)] = false;
    });

    // Set the current category checkbox
    newCheckedCategories[categoryId] = checked;
    setCheckedCategories(newCheckedCategories);

    // Update URL params only if not in mobile mode
    if (!isMobile) {
      const currentParams = new URLSearchParams(searchParams.toString());
      currentParams.delete("id");
      if (checked) {
        const selectedCategory = categoryData.find((cat) => cat.id === categoryId);
        if (selectedCategory) {
          const urlFriendlyText = selectedCategory.text.toLowerCase().replace(/\s+/g, "-");
          currentParams.set("category", urlFriendlyText);
        }
      } else {
        currentParams.delete("category");
      }
      setSearchParams(currentParams, { replace: true });
    }

    // Call parent callback if provided
    if (onApplyCategoryFilter) {
      onApplyCategoryFilter(checked ? categoryId : "");
    }
  };

  const handleSourceChange = (sourceId: number, checked: boolean) => {
    const newCheckedSources = { ...checkedSources };

    // Clear all other source checkboxes (since only one source can be selected at a time)
    Object.keys(newCheckedSources).forEach((key) => {
      newCheckedSources[parseInt(key)] = false;
    });

    // Set the current source checkbox
    newCheckedSources[sourceId] = checked;
    setCheckedSources(newCheckedSources);

    // Update URL params only if not in mobile mode
    if (!isMobile) {
      const currentParams = new URLSearchParams(searchParams.toString());
      currentParams.delete("id");
      if (checked) {
        const selectedSource = chopaiSource?.find((src) => src.id === sourceId);
        if (selectedSource) {
          const urlFriendlyText = selectedSource.text.toLowerCase().replace(/\s+/g, "-");
          currentParams.set("source", urlFriendlyText);
        }
      } else {
        currentParams.delete("source");
      }
      setSearchParams(currentParams, { replace: true });
    }

    // Call parent callback if provided
    if (onApplySourceFilter) {
      onApplySourceFilter(checked ? sourceId : "");
    }
  };

  const handleClearAll = () => {
    // In mobile mode, don't touch URL parameters or internal state
    if (!isMobile) {
      setCheckedItems({});
      setCheckedCategories({});

      const currentParams = new URLSearchParams(searchParams.toString());
      currentParams.delete("type");
      currentParams.delete("category");
      currentParams.delete("id");
      currentParams.delete("source");
      setSearchParams(currentParams, { replace: true });
    }

    if (onClearAllFilters) {
      onClearAllFilters();
    }
  };

  return (
    <div className="w-full h-auto flex flex-col justify-start items-center gap-3 md:gap-2">
      <div className="w-full flex justify-between items-center lg:sticky lg:top-0 lg:bg-primary lg:py-0 px-1.5">
        <span className={cn("body_text_l text-textColor", medium)}>{t(`chopai.filter`)}</span>
        <button
          className={cn("text-size hidden lg:flex text-textColor font-normal cursor-pointer body_text_l", normalInter)}
          onClick={handleClearAll}
        >
          {t(`chopai.clear_all`)}
        </button>
        <SheetClose className="flex lg:hidden">
          <IoMdClose size={20} className="text-textColor" />
        </SheetClose>
      </div>

      <div className="w-full flex flex-col justify-start items-center gap-3">
        <Accordion type="single" collapsible defaultValue="type" className="w-full border-[1px] border-borderPrimary rounded-xl p-4 bg-primary">
          <AccordionItem value={"type"} className="w-full">
            <AccordionTrigger className="w-full flex justify-between items-center" iconVisible={false}>
              <div className="w-full flex justify-between items-center">
                <span className={cn("body_text_m text-textColor", medium)}>{t(`chopai.type`)}</span>
                <FaChevronDown
                  size={15}
                  color={"#572713"}
                  className={`transition-transform duration-300 shrink-0 text-muted-foreground rotate-180 group-data-[state=open]:rotate-0`}
                />
              </div>
            </AccordionTrigger>
            <AccordionContent className="w-full">
              <div className="w-full mt-2 pt-4 border-t-2 border-t-borderPrimary flex flex-col justify-start items-start gap-2">
                {dohaChopaiFilterData.map((item) => {
                  // In mobile mode, use tempFilters state; otherwise use checkedItems from URL
                  const isChecked = isMobile ? tempFilters?.type === item.type : checkedItems[item.type] || false;
                  return (
                    <div className="flex items-center space-x-2" key={item.id}>
                      <div className="flex flex-row justify-start items-center space-x-3 space-y-0">
                        <ClientOnly
                          fallback={
                            <>
                              <div className="peer w-[18px] h-[18px] border-[1px] border-borderPrimary rounded-full bg-none shrink-0" />
                              <label
                                className={cn("body_text_s text-textColor line-clamp-1 cursor-pointer", normalInter)}
                                title={lang === LANG.gujarati ? item.guName : item.enName}
                              >
                                {lang === LANG.gujarati ? item.guName : item.enName}
                              </label>
                            </>
                          }
                        >
                          <div className="flex items-center space-x-2" key={item.id}>
                            <button
                              className="flex flex-row justify-start items-center space-x-3 space-y-0 cursor-pointer"
                              onClick={() => handleCheckboxChange(item.type, !isChecked)}
                            >
                              <div className="w-[18px] h-[18px] rounded-full border-[2px] border-borderPrimary flex items-center justify-center flex-shrink-0 leading-none">
                                {isChecked && <div className="w-2 h-2 rounded-full bg-textColor flex-shrink-0" />}
                              </div>
                              <span
                                className={cn("body_text_s text-textColor line-clamp-1", normalInter)}
                                title={lang === LANG.gujarati ? item.guName : item.enName}
                              >
                                {lang === LANG.gujarati ? item.guName : item.enName}
                              </span>
                            </button>
                          </div>
                        </ClientOnly>
                      </div>
                    </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Category Accordion */}
        {categoryData.length ? (
          <Accordion type="single" defaultValue="category" collapsible className="w-full border-[1px] border-borderPrimary rounded-xl p-4 bg-primary">
            <AccordionItem value={"category"} className="w-full">
              <AccordionTrigger className="w-full flex justify-between items-center" iconVisible={false}>
                <div className="w-full flex justify-between items-center">
                  <span className={cn("body_text_m text-textColor", medium)}>{t(`chopai.category`)}</span>
                  <FaChevronDown
                    size={15}
                    color={"#572713"}
                    className={`transition-transform duration-300 shrink-0 text-muted-foreground rotate-180 group-data-[state=open]:rotate-0`}
                  />
                </div>
              </AccordionTrigger>
              <AccordionContent className="w-full">
                <div className="w-full mt-2 pt-4 border-t-2 border-t-borderPrimary flex flex-col justify-start items-start gap-2">
                  {(showAllCategories ? categoryData : categoryData.slice(0, INITIAL_CATEGORY_DISPLAY)).map((item) => {
                    // In mobile mode, use tempFilters state; otherwise use checkedCategories from URL
                    const isChecked = isMobile ? tempFilters?.category === item.id : checkedCategories[item.id] || false;
                    return (
                      <div className="flex items-center space-x-2" key={item.id}>
                        <div className="flex flex-row justify-start items-center space-x-3 space-y-0">
                          <ClientOnly
                            fallback={
                              <>
                                <div className="peer w-[18px] h-[18px] border-[1px] border-borderPrimary rounded-full bg-none shrink-0" />
                                <label className={cn("body_text_s text-textColor line-clamp-1 cursor-pointer", normalInter)} title={item.text}>
                                  {item.text}
                                </label>
                              </>
                            }
                          >
                            <div className="flex items-center space-x-2" key={item.id}>
                              <button
                                className="flex flex-row justify-start items-center space-x-3 space-y-0 cursor-pointer"
                                onClick={() => handleCategoryChange(item.id, !isChecked)}
                              >
                                <div className="w-[18px] h-[18px] rounded-full border-[2px] border-borderPrimary flex items-center justify-center flex-shrink-0 leading-none">
                                  {isChecked && <div className="w-2 h-2 rounded-full bg-textColor flex-shrink-0" />}
                                </div>
                                <span className={cn("body_text_s text-textColor line-clamp-1", normalInter)} title={item.text}>
                                  {item.text}
                                </span>
                              </button>
                            </div>
                          </ClientOnly>
                        </div>
                      </div>
                    );
                  })}

                  {/* See More / Show Less Button */}
                  {categoryData.length > INITIAL_CATEGORY_DISPLAY && (
                    <button
                      className="mt-2 body_text_s text-primaryColor font-inter font-medium cursor-pointer hover:underline"
                      onClick={() => setShowAllCategories(!showAllCategories)}
                    >
                      {showAllCategories ? `Show Less` : `${categoryData.length - INITIAL_CATEGORY_DISPLAY} More`}
                    </button>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ) : null}

        {/* Source Accordion */}
        {chopaiSource.length ? (
          <Accordion type="single" defaultValue="source" collapsible className="w-full border-[1px] border-borderPrimary rounded-xl p-4 bg-primary">
            <AccordionItem value={"source"} className="w-full">
              <AccordionTrigger className="w-full flex justify-between items-center" iconVisible={false}>
                <div className="w-full flex justify-between items-center">
                  <span className={cn("body_text_m text-textColor", medium)}>{t(`chopai.source`)}</span>
                  <FaChevronDown
                    size={15}
                    color={"#572713"}
                    className={`transition-transform duration-300 shrink-0 text-muted-foreground rotate-180 group-data-[state=open]:rotate-0`}
                  />
                </div>
              </AccordionTrigger>
              <AccordionContent className="w-full">
                <div className="w-full mt-2 pt-4 border-t-2 border-t-borderPrimary flex flex-col justify-start items-start gap-2">
                  {(showAllCategories ? chopaiSource : chopaiSource?.slice(0, INITIAL_CATEGORY_DISPLAY))?.map((item) => {
                    // In mobile mode, use tempFilters.source; otherwise use checkedSources from URL
                    const isChecked = isMobile ? tempFilters?.source === item.id : checkedSources[item.id] || false;
                    return (
                      <div className="flex items-center space-x-2" key={item.id}>
                        <div className="flex flex-row justify-start items-center space-x-3 space-y-0">
                          <ClientOnly
                            fallback={
                              <>
                                <div className="peer w-[18px] h-[18px] border-[1px] border-borderPrimary rounded-full bg-none shrink-0" />
                                <label className={cn("body_text_s text-textColor line-clamp-1 cursor-pointer", normalInter)} title={item.text}>
                                  {item.text}
                                </label>
                              </>
                            }
                          >
                            <div className="flex items-center space-x-2" key={item.id}>
                              <button
                                className="flex flex-row justify-start items-center space-x-3 space-y-0 cursor-pointer"
                                onClick={() => handleSourceChange(item.id, !isChecked)}
                              >
                                <div className="w-[18px] h-[18px] rounded-full border-[2px] border-borderPrimary flex items-center justify-center flex-shrink-0 leading-none">
                                  {isChecked && <div className="w-2 h-2 rounded-full bg-textColor flex-shrink-0" />}
                                </div>
                                <span className={cn("body_text_s text-textColor line-clamp-1", normalInter)} title={item.text}>
                                  {item.text}
                                </span>
                              </button>
                            </div>
                          </ClientOnly>
                        </div>
                      </div>
                    );
                  })}

                  {/* See More / Show Less Button */}
                  {chopaiSource && chopaiSource?.length > INITIAL_CATEGORY_DISPLAY && (
                    <button
                      className="mt-2 body_text_s text-primaryColor font-inter font-medium cursor-pointer hover:underline"
                      onClick={() => setShowAllCategories(!showAllCategories)}
                    >
                      {showAllCategories ? `Show Less` : `${chopaiSource.length - INITIAL_CATEGORY_DISPLAY} More`}
                    </button>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ) : null}
      </div>
      {isMobile && (
        <div className="w-full flex justify-between items-center lg:hidden sticky bottom-0 bg-primary">
          <button
            className="w-[40%] bg-lightbackground rounded-full py-2 mx-auto flex justify-center items-center text-textColor btn_text font-inter font-normal"
            onClick={handleClearAll}
          >
            {t(`chopai.clear`)}
          </button>

          <SheetClose
            onClick={handleApplyFilters}
            className="w-[40%] bg-textHeadingColor rounded-full py-2 mx-auto flex justify-center items-center text-white btn_text font-inter font-normal"
          >
            {t(`chopai.apply`)}
          </SheetClose>
        </div>
      )}
    </div>
  );
};

export default DohaChopaiFilter;
